<template>
  <div>
    <a
      :class="{ 'btn-default': !unsaved, 'btn-success': unsaved }"
      href="#"
      class="btn btn-default"
      style="position: absolute; top: -40px; left: 0"
      @click="open = true"
    >
      {{ $t('Update cover picture') }}
      <i v-if="unsaved" class="fa fa-star" />
    </a>
    <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
    <BaseModal
      :disabled="loading"
      :header-text="$t('Cover picture')"
      :remove-cancel-button="true"
      :confirm-button-text="$t('Close')"
      :confirm-button-class="'btn btn-default btn-block'"
      :is-open="open"
      :modal-id="'cover'"
      @cancel="open = false"
      @save="open = false"
    >
      <br />
      <p>{{ $t('Here you can set your cover picture on your profile.') }}</p>
      <p>
        {{ $t('You can upload your own picture or you can choose from our selection of pictures') }}
      </p>

      <hr />
      <div class="row">
        <div class="col-sm-6 m-b-20">
          <label>{{ $t('Upload your own') }}</label>
          <br />
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <BaseHtmlForm
            v-if="isMe"
            ref="form"
            method="PATCH"
            action="/profile/cover"
            enctype="multipart/form-data"
            append-user-and-account
          >
            <label for="input-coverpicture" class="btn btn-success" @click="clearSelectedFile">
              {{ $t('Select picture') }}
              <input
                id="input-coverpicture"
                ref="file"
                :disabled="loading"
                accept="image/gif, image/jpeg, image/png"
                name="coverpicture"
                type="file"
                style="display: none"
                @change="fileAdded"
              />
              <input type="hidden" name="MAX_FILE_SIZE" value="200000" />
            </label>
          </BaseHtmlForm>
          <br />
          <small v-if="error" class="c-red">
            <br />
            <strong>{{ error }}</strong>
          </small>
          <small class="c-gray">
            <br />
            {{ $t('Do not use a portrait as a background - it does not work') }}
            :)
          </small>
        </div>
        <div class="col-sm-6">
          <label>{{ $t('Or choose one of ours') }}</label>
          <br />
          <input type="hidden" name="selected_coverpicture" />
          <button
            :disabled="loading"
            class="btn btn-info browse-coverpictures"
            @click="getAndDisplayCoverImages"
          >
            {{ $t('Choose from our backgrounds') }}
          </button>
          <br />
        </div>
        <div class="col-xs-12 text-center">
          <div v-if="fileInfo" id="input-upload-ready">
            <div>
              <img :src="fileSrc" />
            </div>
            <br />
            <button :disabled="loading" class="btn btn-primary" @click="saveCustomFile">
              <transition name="fade">
                <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
                <button-spinner v-if="loading" color="white" />
              </transition>
              {{ $t('Save and continue') }}
            </button>
            <button :disabled="loading" class="btn btn-default c-red" @click="annulled">
              {{ $t('Cancel') }}
            </button>
          </div>
          <div v-if="hasGeneric" id="selected-coverpicture-ready">
            <div>
              <img :src="generic" />
            </div>
            <br />
            <button :disabled="loading" class="btn btn-primary" @click="saveGenericFile">
              <transition name="fade">
                <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
                <button-spinner v-if="loading" color="white" />
              </transition>
              {{ $t('Save and continue') }}
            </button>
            <button :disabled="loading" class="btn btn-default c-red" @click="annulled">
              {{ $t('Cancel') }}
            </button>
          </div>
        </div>
      </div>
      <template v-if="hasImage">
        <hr />

        <div class="row">
          <div class="m-t-10 col-sm-offset-2 col-sm-8 text-center">
            <em>{{ $t('Your current cover picture') }}</em>
            <div class="preview-coverpicture">
              <img :src="coverImage" :class="{ blur: loading }" @load="loading = false" />
            </div>
            <button :disabled="loading" class="btn btn-xs btn-default" @click="removeCurrentFile">
              <span class="glyphicon glyphicon-remove-sign" aria-hidden="true" />
              {{ $t('Remove') }}
            </button>
          </div>
        </div>
      </template>
    </BaseModal>
    <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
    <BaseModal
      :header-text="$t('Select a cover picture')"
      :modal-id="'coverimages'"
      :remove-cancel-button="true"
      :confirm-button-text="$t('Cancel')"
      :is-open="trigger"
      @cancel="closeCoverOptions"
      @save="closeCoverOptions"
      @positive-response="closeCoverOptions"
      @negative-response="closeCoverOptions"
    >
      {{ $t('Click on an image to select it.') }}
      <div id="browse-coverpictures-wrap">
        <ul class="browse-photos">
          <li
            v-for="(image, i) in coverImages"
            :key="image"
            :class="{ firstinrow: i % 3 === 0 }"
            class="coverpicture"
          >
            <a
              :id="'coverpicture_' + i"
              href="#"
              class="select-coverpicture"
              @click="selectedGeneric(image)"
            >
              <VLazyImage :src="image" />
            </a>
          </li>
        </ul>
        <div class="clearfix" />
      </div>
    </BaseModal>
  </div>
</template>

<script setup>
import VLazyImage from 'v-lazy-image'
</script>

<script>
import axios from 'axios'
import ax from '@/api/axios-instance'
import enums from '@/utils/enum'
import { SET_EDIT_UNSAVED } from '@/state/mutation-types'

export default {
  name: 'EditCoverImage',
  props: {
    isMe: {
      required: true,
      type: Boolean,
    },
    currentCover: {
      required: false,
      type: String,
      default: '',
    },
  },
  data() {
    return {
      currentCoverMutable: '',
      timestamp: Date.now(),
      coverImages: [],
      trigger: false,
      fileInfo: null,
      fileSrc: null,
      generic: null,
      hasGeneric: false,
      unsaved: false,
      open: false,
      loadedGeneric: false,
      loading: false,
      useProp: true,
      hasImage: false,
      error: '',
      ax: axios.create({
        baseURL: '',
        headers: ax.headers,
      }),
    }
  },
  computed: {
    url() {
      return '/profile/cover'
    },
    coverImage() {
      if (this.useProp) {
        return this.currentCoverMutable
      }
      return this.loadedGeneric
        ? this.generic
        : `/profile/${this.account.profile.public_id}/coverpicture?${this.timestamp}`
    },
  },
  watch: {
    '$store.getters.watchEdit.on': function (edit, old) {
      if (!edit && old && this.unsaved) {
        if (this.hasGeneric) {
          this.saveGenericFile()
        } else {
          this.saveCustomFile()
        }
      }
    },
  },
  created() {
    // Here we copy our axios instance so that we can override som of our default values,
    // without having an impact on any of the other parts of the code
    this.hasImage = this.currentCover !== ''
    this.currentCoverMutable = this.currentCover
  },
  methods: {
    err() {
      this.$store.commit('sendNotification', {
        type: enums.notification.types.ERROR,
        sender: this,
        severity: enums.notification.severity.CRITICAL,
      })
    },
    updateTimeStamp() {
      this.timestamp = Date.now()
    },
    updateBackGroundImage() {
      const images = document.querySelectorAll('.profile-banner img')
      if (images.length > 0) {
        const image = images[0]
        // Here we just figure out if we have a reason to actually update the background
        if (this.hasImage || this.fileSrc || (this.generic && this.hasGeneric)) {
          let src = this.coverImage
          if (this.fileSrc) {
            src = this.fileSrc
          } else if (this.generic && this.hasGeneric) {
            src = this.generic
          }
          image.src = src
        } else {
          image.src = ''
        }
      }
    },
    clearSelected() {
      this.fileInfo = null
      this.fileSrc = null
      this.hasGeneric = false
      this.loading = false
    },
    finishedSaving() {
      this.currentCoverMutable = this.coverImage
      this.useProp = true
      this.$store.commit(SET_EDIT_UNSAVED, false)
      this.unsaved = false
      this.loading = false
    },
    annulled() {
      if (this.loading) {
        return
      }
      this.clearSelected()
      this.useProp = true
      this.updateBackGroundImage()
      this.$store.commit(SET_EDIT_UNSAVED, false)
      this.unsaved = false
    },

    async saveCustomFile() {
      // We don't want to have two instances running at the same time
      if (this.loading) {
        return
      }
      try {
        this.loading = true
        // We convert the image into form data format, as that is how the backend handles it
        const fileData = new FormData(this.$refs.form.$el)
        const res = await this.ax.post(this.url, fileData, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
        this.loadedGeneric = false
        this.hasImage = true
        this.clearSelected()
        this.finishedSaving()
        this.updateBackGroundImage()
        if (res.data.success) {
          this.updateTimeStamp()
          this.currentCoverMutable = res.data.cover_picture_path
          this.updateBackGroundImage()
        } else {
          this.error = res.data.message
          this.err()
        }
      } catch (e) {
        this.loading = false
        this.err()
        throw e
      }
    },
    async saveGenericFile() {
      if (this.loading) {
        return
      }
      try {
        this.loading = true
        await this.ax.post(
          this.url,
          {
            selected_coverpicture: this.generic,
            _method: 'PATCH',
          },
          null,
          {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          },
        )
        this.loadedGeneric = true
        this.hasImage = true
        this.useProp = false
        this.updateTimeStamp()
        this.clearSelected()
        this.updateBackGroundImage()
        this.finishedSaving()
      } catch (e) {
        this.loading = false
        this.err()
        throw e
      }
    },
    removeCurrentFile() {
      if (this.loading) {
        return
      }
      /* eslint-disable-next-line no-console */
      window.logStr = console.log // Needs to be here or else swal fails somehow
      swal(
        {
          title: $t('Are you sure?'),
          text: '',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: $t('Yes, delete'),
          cancelButtonText: $t('No, go back!'),
          closeOnConfirm: false,
        },
        async () => {
          swal($t('Processing'), $t('Now deleting'), 'success')
          try {
            this.$store.commit(SET_EDIT_UNSAVED, true)
            this.loading = true
            const response = await this.ax.post(
              this.url,
              {
                _method: 'DELETE',
              },
              {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
              },
            )
            swal($t('Success'), response?.data?.message ?? 'Removed', 'success')
            this.hasImage = false
            this.useProp = false
            this.updateTimeStamp()
            this.updateBackGroundImage()
            this.finishedSaving()
          } catch (e) {
            this.loading = false
            this.err()
            throw e
          }
        },
      )
    },
    clearSelectedFile() {
      this.$refs.file.value = ''
    },
    fileAdded() {
      this.error = ''
      this.fileInfo = this.$refs.file.files[0] || false
      if (!this.fileInfo) {
        return
      }
      if (this.fileInfo.size > 2000000) {
        this.error = $t('You cannot upload files bigger than :size.', {
          size: '2MB',
        })
        this.clearSelected()
        return
      }
      this.$store.commit(SET_EDIT_UNSAVED, true)
      this.unsaved = true
      const Reader = new FileReader()
      Reader.onload = (e) => {
        this.fileSrc = e.target.result
        this.updateBackGroundImage()
      }
      Reader.readAsDataURL(this.fileInfo)
      this.hasGeneric = false
      this.generic = null
    },
    selectedGeneric(src) {
      if (this.loading) {
        return
      }
      // We add timestamp to reload some information
      this.$store.commit(SET_EDIT_UNSAVED, true)
      this.unsaved = true
      this.generic = `${src}?${Date.now()}`
      this.hasGeneric = true
      this.fileSrc = null
      this.fileInfo = null
      this.updateBackGroundImage()
      this.closeCoverOptions()
    },
    closeCoverOptions() {
      this.trigger = false

      // Interval is setup to reattach modal-open to the body so that we can keep scrolling on our
      // parent modal. This is needed since bootstrap automatically removes modal-open when
      // a modal closes, even if more than one modal is open at any given moment in time.
      setInterval(() => {
        document.body.classList.add('modal-open')
      }, 10)
    },
    async getAndDisplayCoverImages() {
      if (this.loading) {
        return
      }
      if (this.locked) {
        return
      }
      this.locked = true

      if (this.coverImages.length === 0) {
        this.coverImages = (await axios.get('/browsecoverpicturesfeed')).data
      }
      this.trigger = true
      this.locked = false
    },
  },
}
</script>

<style scoped lang="scss">
img {
  max-height: 100%;
}
.modal-dialog {
  overflow-y: auto;
}
.blur {
  filter: blur(3px);
}
</style>
